/* eslint-disable prettier/prettier */
import {
  ChartOptions,
  LegendItem,
  ChartDataset,
  ChartData,
  Chart,
} from 'chart.js';
import {
  PartialEventContext,
  LabelPosition,
  AnnotationOptions,
} from 'chartjs-plugin-annotation';
import { TFunction } from 'i18next';
import fromPairs from 'lodash/fromPairs';
import noop from 'lodash/noop';
import toPairs from 'lodash/toPairs';
import uniqBy from 'lodash/uniqBy';

import { formatCurrency, IRequiredStatistics } from '../../lib';

/**
 * Results chart annotation constants and builders.
 *
 * Overlay/label positioning fixes (pushed to master):
 * - calculateLabelPosition: labels avoid overlapping the chart by using a left-edge
 *   threshold (22.5% or 100px min) and end-position detection so "Low value" / "High value"
 *   stay readable. Quartile boundary labels can pass skipEdgeAdjustment to stay centered on
 *   their tick marks. LQ display band is 10%–35% (was 15%–35%) so left labels stay
 *   readable; LQ label text uses x:'start' so it grows right from the marker.
 * - Quartile spacing: adjustQuartileSpacing, applyExtraNudge, and display-position
 *   helpers (computeLowHighDisplayPositions, computeMiddleDisplayPositions) keep
 *   quartile and mean labels separated from each other and from the middle on the
 *   log scale, preventing label overlay on the graph.
 */

export const calculateLabelPosition = (
  context: PartialEventContext,
  defaultPosition: 'start' | 'end' | 'center' = 'center',
  options?: { skipEdgeAdjustment?: boolean },
) => {
  if (options?.skipEdgeAdjustment) {
    return { x: defaultPosition };
  }

  const chartSize = context.chart.width;
  // Push label to the right if it's within 22.5% of the left edge (increased from 15%)
  // Also use a minimum pixel threshold to handle narrow charts
  const leftThreshold = Math.max(chartSize * 0.225, 100);
  const startPos = Number(context.element?.x) < leftThreshold && 'start';
  const endPos = Number(context.element?.x2) > chartSize && 'end';

  return {
    x: (startPos || endPos || defaultPosition) as LabelPosition,
  };
};

const MIN_DISPLAYABLE_VALUE = 0;

/** Logarithmic axes require min > 0; 0 collapses left-side labels. */
const MIN_LOG_AXIS_VALUE = 1;

// tune these if you want more/less spacing
const BASE_OFFSET_FRACTION = 0.08;    // your original 8%
const MIN_NORM_GAP = 0.03;            // 3% of the axis width
const MAX_OFFSET_MULTIPLIER = 4;      // don’t go crazy
const OFFSET_MULTIPLIER_STEP = 0.5;   // how aggressively to bump

// Helper function to adjust low/high quartile spacing based on middle-half mean
const adjustQuartileSpacing = (
  initialLq: number,
  initialHq: number,
  middleHalfMeanNoZeros: number,
  computeShiftedQuartiles: (multiplier: number) => { lq: number; hq: number },
  toNormalized: (value: number) => number,
): { lq: number; hq: number } => {
  const mid = middleHalfMeanNoZeros;
  const normMid = toNormalized(mid);

  let offsetMultiplier = 1;
  let adjustedQuartiles = computeShiftedQuartiles(offsetMultiplier);

  const isTooCloseToMid = (quartiles: { lq: number; hq: number }) => {
    const normLq = toNormalized(quartiles.lq);
    const normHq = toNormalized(quartiles.hq);

    const diffLq = Math.abs(normLq - normMid);
    const diffHq = Math.abs(normHq - normMid);

    return diffLq < MIN_NORM_GAP || diffHq < MIN_NORM_GAP;
  };

  // bump the offset until the low/high quartile markers are far enough from the mid
  while (
    offsetMultiplier < MAX_OFFSET_MULTIPLIER &&
    isTooCloseToMid(adjustedQuartiles)
  ) {
    offsetMultiplier += OFFSET_MULTIPLIER_STEP;
    adjustedQuartiles = computeShiftedQuartiles(offsetMultiplier);
  }

  return adjustedQuartiles;
};

// Helper function to apply extra nudge to high quartile if needed
const applyExtraNudge = (
  highestQuartileValue: number,
  middleHalfMeanNoZeros: number,
  max: number,
  toNormalized: (value: number) => number,
  logMin: number,
  logRange: number,
): number => {
  const normMid = toNormalized(middleHalfMeanNoZeros);
  const normHq = toNormalized(highestQuartileValue);

  const EXTRA_NUDGE = 0.02; // 2% of axis width

  // if high quartile is still too close, push it further right
  if (normHq - normMid < MIN_NORM_GAP + EXTRA_NUDGE) {
    const targetNormHq = normMid + (MIN_NORM_GAP + EXTRA_NUDGE);

    const fromNormalized = (norm: number) =>
      10 ** (logMin + norm * logRange);

    const boostedHq = fromNormalized(targetNormHq);

    if (boostedHq < max) {
      return boostedHq;
    }
  }

  return highestQuartileValue;
};

const clamp = (v: number, a: number, b: number) =>
  Math.max(a, Math.min(b, v));

const TARGET_MID = 0.5;
const NUDGE = 0.25;

/**
 * Match production label layout bands, with a 10% LQ floor so left-edge labels
 * stay readable when Low value is $0 and High Value is extreme.
 */
function computeLowHighDisplayPositions(
  lowestQuartileValue: number,
  highestQuartileValue: number,
  toNormalized: (value: number) => number,
  fromNormalized: (norm: number) => number,
) {
  const trueNormLow = toNormalized(lowestQuartileValue);
  const displayNormLow = clamp(trueNormLow, 0.1, 0.35);
  const displayPositionLowestQuartile = fromNormalized(displayNormLow);
  const trueNormHigh = toNormalized(highestQuartileValue);
  const displayNormHigh = clamp(trueNormHigh, 0.65, 0.95);
  const displayPositionHighestQuartile = fromNormalized(displayNormHigh);
  return { displayPositionLowestQuartile, displayPositionHighestQuartile };
}

function computeMiddleDisplayPositions(
  middleHalfMeanNoZeros: number | null | undefined,
  overallAverageNoZeros: number | null | undefined,
  toNormalized: (value: number) => number,
  fromNormalized: (norm: number) => number,
): {
  displayPositionMiddleHalf: number | null;
  displayPositionMean: number | null;
} {
  if (middleHalfMeanNoZeros != null && overallAverageNoZeros != null) {
    const trueMid = toNormalized(middleHalfMeanNoZeros);
    const trueMean = toNormalized(overallAverageNoZeros);
    const midHalfBase = trueMean <= trueMid ? 0.52 : 0.48;
    const meanBase = trueMean <= trueMid ? 0.48 : 0.52;
    const midHalfDisplayNorm = clamp(
      midHalfBase + (trueMid - midHalfBase) * NUDGE,
      0.4,
      0.6,
    );
    const meanDisplayNorm = clamp(
      meanBase + (trueMean - meanBase) * NUDGE,
      0.4,
      0.6,
    );
    return {
      displayPositionMiddleHalf: fromNormalized(midHalfDisplayNorm),
      displayPositionMean: fromNormalized(meanDisplayNorm),
    };
  }
  if (middleHalfMeanNoZeros != null) {
    const trueMid = toNormalized(middleHalfMeanNoZeros);
    const midHalfDisplayNorm = clamp(
      TARGET_MID + (trueMid - TARGET_MID) * NUDGE,
      0.4,
      0.6,
    );
    return {
      displayPositionMiddleHalf: fromNormalized(midHalfDisplayNorm),
      displayPositionMean: null,
    };
  }
  if (overallAverageNoZeros != null) {
    const trueMean = toNormalized(overallAverageNoZeros);
    const meanDisplayNorm = clamp(
      TARGET_MID + (trueMean - TARGET_MID) * NUDGE,
      0.4,
      0.6,
    );
    return {
      displayPositionMiddleHalf: null,
      displayPositionMean: fromNormalized(meanDisplayNorm),
    };
  }
  return { displayPositionMiddleHalf: null, displayPositionMean: null };
}

const getDataValues = (data: IRequiredStatistics) => {
  const {
    overallMinimumPublic,
    overallMaximumPublic,
    lowestQuartileAverageNoZeros,
    highestQuartileAverageNoZeros,
    middleHalfMeanNoZeros,
    overallAverageNoZeros,
  } = data;

  const publicMin = Math.min(
    overallMinimumPublic,
    lowestQuartileAverageNoZeros,
  );
  const publicMax = Math.max(
    overallMaximumPublic,
    highestQuartileAverageNoZeros,
  );

  const min = calculateMin(publicMin);
  const max = calculateMax(publicMax);

  const isMaximumsEqual =
    overallMaximumPublic === highestQuartileAverageNoZeros;

  const lowestQuartileValueRaw = Number(lowestQuartileAverageNoZeros);
  const highestQuartileBase = isMaximumsEqual
    ? max
    : Number(highestQuartileAverageNoZeros);

  const baseOffset = (publicMax - publicMin) * BASE_OFFSET_FRACTION;

  const computeShiftedQuartiles = (multiplier: number) => {
    const offset = baseOffset * multiplier;
    let lq = lowestQuartileValueRaw - offset;
    let hq = highestQuartileBase + offset;
    if (lq < min) {
      lq = lowestQuartileValueRaw;
    }
    if (hq > max) {
      hq = highestQuartileBase;
    }
    return { lq, hq };
  };

  const logMin = Math.log10(Math.max(min, MIN_LOG_AXIS_VALUE));
  const logMax = Math.log10(Math.max(max, MIN_LOG_AXIS_VALUE * 10));
  const logRange = logMax - logMin || 1;

  const toNormalized = (value: number) => {
    if (!value || value <= 0) {
      return 0;
    }
    return (Math.log10(value) - logMin) / logRange;
  };

  const fromNormalized = (norm: number) =>
    10 ** (logMin + norm * logRange);

  let { lq, hq } = computeShiftedQuartiles(1);

  if (middleHalfMeanNoZeros) {
    ({ lq, hq } = adjustQuartileSpacing(
      lq,
      hq,
      middleHalfMeanNoZeros,
      computeShiftedQuartiles,
      toNormalized,
    ));
  }

  let highestQuartileValue = hq;
  if (middleHalfMeanNoZeros) {
    highestQuartileValue = applyExtraNudge(
      highestQuartileValue,
      middleHalfMeanNoZeros,
      max,
      toNormalized,
      logMin,
      logRange,
    );
  }

  const { displayPositionLowestQuartile, displayPositionHighestQuartile } =
    computeLowHighDisplayPositions(
      lq,
      highestQuartileValue,
      toNormalized,
      fromNormalized,
    );

  const { displayPositionMiddleHalf, displayPositionMean } =
    computeMiddleDisplayPositions(
      middleHalfMeanNoZeros,
      overallAverageNoZeros,
      toNormalized,
      fromNormalized,
    );

  return {
    publicMin,
    publicMax,
    min,
    max,
    meanValue: overallAverageNoZeros,
    middleHalfMeanValue: middleHalfMeanNoZeros,
    displayPositionLowestQuartile,
    displayPositionHighestQuartile,
    displayPositionMiddleHalf,
    displayPositionMean,
  };
};



const generateLabels = (chart: Chart) => {
  const {
    data: { datasets },
  } = chart;

  const uniqueLabelDatasets = uniqBy(datasets, (item) => item.label);
  return uniqueLabelDatasets.map(
    (item) =>
      ({
        text: item.label ?? '',
        fillStyle: item.backgroundColor,
        strokeStyle: item.borderColor,
        lineWidth: item.borderWidth,
        fontColor: '#898E9A',
      }) as LegendItem,
  );
};

export const buildAnnotations = (
  data: IRequiredStatistics,
): Array<AnnotationOptions> => {
  const {
    publicMin,
    publicMax,
    min,
    max,
    meanValue,
    middleHalfMeanValue,
    displayPositionLowestQuartile,
    displayPositionHighestQuartile,
    displayPositionMiddleHalf,
    displayPositionMean,
  } = getDataValues(data);

  const annotations: Array<AnnotationOptions> = [
    // start of graph value
    {
      id: 'startOfGraphValue',
      type: 'label',
      content: data.bottomConfidentialCasesCount
        ? ''
        : formatCurrency(publicMin),
      xScaleID: 'x',
      xValue: min,
      yAdjust: 30,
      font: {
        family: 'Lato',
        size: 16,
        weight: '600',
      },
      position: {
        x: 'start',
        y: '0%',
      },
      z: 2,
    },

    {
      id: 'startOfGraphValueLabel',
      type: 'label',
      content: ['Low value'],
      xScaleID: 'x',
      xMin: min,
      xMax: min,
      yAdjust: 70,
      font: {
        family: 'Lato',
        size: 14,
        weight: '500',
      },
      position: (context: PartialEventContext) =>
        calculateLabelPosition(context, 'start'),
    },

    // end of graph value
    {
      id: 'endOfGraphValue',
      type: 'label',
      content: data.topConfidentialCasesCount ? '' : formatCurrency(publicMax),
      xScaleID: 'x',
      xValue: max,
      yAdjust: 21,
      font: {
        family: 'Lato',
        size: 16,
        weight: '600',
      },
      position: {
        x: 'end',
        y: '0%',
      },
      z: 2,
    },
    //start of graph
    {
      id: 'startOfGraph',
      type: 'line',
      xScaleID: 'x',
      xMin: min,
      xMax: min,
      borderColor: '#898E9A',
      borderWidth: 4,
      yMin: -0.039,
      yMax: 0.039,
      z: 1,
    },
    //end of graph
    {
      id: 'endOfGraph',
      type: 'line',
      xScaleID: 'x',
      xMin: max,
      xMax: max,
      borderColor: '#898E9A',
      borderWidth: 4,
      yMin: -0.039,
      yMax: 0.039,
      z: 3,
    },
    {
      id: 'lowestQuartileAverageLabel',
      type: 'label',
      content: formatCurrency(data.lowestQuartileAverageNoZeros),
      xScaleID: 'x',
      xMin: displayPositionLowestQuartile,
      xMax: displayPositionLowestQuartile,
      yAdjust: -45,
      font: {
        family: 'Lato',
        size: 16,
        weight: '600',
      },
      position: { x: 'start' },
    },
    // highestQuartileAverage label
    {
      id: 'highestQuartileAverageLabel',
      type: 'label',
      content: formatCurrency(data.highestQuartileAverageNoZeros),
      xScaleID: 'x',
      xMin: displayPositionHighestQuartile,
      xMax: displayPositionHighestQuartile,
      yAdjust: -45,
      font: {
        family: 'Lato',
        size: 16,
        weight: '600',
      },
      position: (context: PartialEventContext) =>
        calculateLabelPosition(context, 'center', { skipEdgeAdjustment: true }),
    },
    // start of acceptable range line (at ~25% position)
    {
      id: 'startOfAcceptableRange',
      type: 'line',
      xScaleID: 'x',
      xMin: displayPositionLowestQuartile,
      xMax: displayPositionLowestQuartile,
      borderColor: '#E5B045',
      borderWidth: 2,
      yScaleID: 'annotationY',
      yMin: -0.1,
      yMax: 0.039,
      z: 2,
    },
    {
      id: 'startOfAcceptableRangeLabel',
      type: 'label',
      content: ['Lowest Quartile', 'Mean'],
      xScaleID: 'x',
      xMin: displayPositionLowestQuartile,
      xMax: displayPositionLowestQuartile,
      yAdjust: -80,
      font: {
        family: 'Lato',
        size: 14,
        weight: '500',
      },
      z: 2,
      // Anchor left so the text always reads to the right of the marker
      position: { x: 'start' },
    },
    // end of acceptable range line
    {
      id: 'endOfAcceptableRangeLabel',
      type: 'label',
      content: ['Highest Quartile', 'Mean'],
      xScaleID: 'x',
      xMin: displayPositionHighestQuartile,
      xMax: displayPositionHighestQuartile,
      yScaleID: 'annotationY',
      yAdjust: -80,
      font: {
        family: 'Lato',
        size: 14,
        weight: '500',
      },
      z: 2,
      position: (context: PartialEventContext) =>
        calculateLabelPosition(context, 'center', { skipEdgeAdjustment: true }),
    },
    {
      id: 'endOfAcceptableRange',
      type: 'line',
      xScaleID: 'x',
      xMin: displayPositionHighestQuartile,
      xMax: displayPositionHighestQuartile,
      borderColor: '#E5B045',
      borderWidth: 2,
      yMin: -0.1,
      yMax: 0.039,
      z: 2,
    },
    {
      id: 'startOfBottomConfidentialCasesTendrilLabel',
      type: 'label',
      content: ['High Value'],
      xScaleID: 'x',
      xMin: max,
      xMax: max,
      yAdjust: 70,
      yScaleID: 'annotationY',
      font: {
        family: 'Lato',
        size: 14,
        weight: '500',
      },
      textAlign: 'end',
      z: 2,
      position: (context: PartialEventContext) =>
        calculateLabelPosition(context, 'end'),
    },
  ];

  if (data.bottomConfidentialCasesCount) {
    const center = 10 ** ((Math.log10(min) + Math.log10(publicMin)) / 2);

    annotations.push(
      // count of bottom confidential cases
      {
        id: 'bottomConfidentialCasesCount',
        type: 'label',
        content: String(data.bottomConfidentialCasesCount),
        xScaleID: 'x',
        xValue: center,
        yAdjust: -36,
        color: '#BCBEC3',
        font: {
          family: 'Lato',
          size: 16,
          weight: '500',
        },
        position: (context: PartialEventContext) => ({
          ...calculateLabelPosition(context, 'center'),
          y: '0%',
        }),
      },
      // start tendril of bottom confidential cases
      {
        id: 'startOfBottomConfidentialCasesTendril',
        type: 'line',
        xScaleID: 'x',
        xMin: min,
        xMax: min,
        borderColor: '#898E9A',
        borderWidth: 4,
        yMin: -0.039,
        yMax: 0.039,
        z: 1,
      },
      // end tendril of bottom confidential cases
      {
        id: 'endOfBottomConfidentialCasesTendril',
        type: 'line',
        xScaleID: 'x',
        xMin: data.overallMinimumPublic,
        xMax: data.overallMinimumPublic,
        borderColor: '#898E9A',
        borderWidth: 2,
        yMin: -0.039,
        yMax: 0.1,
        z: 2,
      },
      // value of bottom confidential cases
      {
        id: 'bottomConfidentialCasesValue',
        type: 'label',
        content: formatCurrency(data.overallMinimumPublic),
        xScaleID: 'x',
        xValue: data.overallMinimumPublic,
        yAdjust: 45,
        font: {
          family: 'Lato',
          size: 16,
          weight: '600',
        },
        position: (context: PartialEventContext) =>
          calculateLabelPosition(context, 'center'),
      },
    );
    // backup: optional rendering for: (data.overallMinimumPublic < data.lowestQuartileAverageNoZeros)
  }

  if (data.topConfidentialCasesCount) {
    const center = 10 ** ((Math.log10(max) + Math.log10(publicMax)) / 2);

    annotations.push(
      // count of top confidential cases
      {
        id: 'topConfidentialCasesCount',
        type: 'label',
        content: String(data.topConfidentialCasesCount),
        xScaleID: 'x',
        xValue: center,
        yAdjust: -36,
        color: '#BCBEC3',
        font: {
          family: 'Lato',
          size: 16,
          weight: '500',
        },
        position: (context: PartialEventContext) => ({
          ...calculateLabelPosition(context, 'center'),
          y: '0%',
        }),
      },
      // start tendril of top confidential cases
      {
        id: 'startOfBottomConfidentialCasesTendril',
        type: 'line',
        xScaleID: 'x',
        xMin: data.overallMaximumPublic,
        xMax: data.overallMaximumPublic,
        borderColor: '#898E9A',
        borderWidth: 2,
        yScaleID: 'annotationY',
        yMin: -0.039,
        yMax: 0.1,
        z: 1,
      },
      {
        id: 'startOfBottomConfidentialCasesTendrilLabel',
        type: 'label',
        content: ['High Value'],
        xScaleID: 'x',
        xMin: data.overallMaximumPublic,
        xMax: data.overallMaximumPublic,
        yScaleID: 'annotationY',
        yAdjust: 70,
        font: {
          family: 'Lato',
          size: 14,
          weight: '500',
        },
        z: 2,
        position: (context: PartialEventContext) =>
          calculateLabelPosition(context, 'center'),
      },
      // end tendril of top confidential cases
      {
        id: 'endOfBottomConfidentialCasesTendril',
        type: 'line',
        xScaleID: 'x',
        xMin: max,
        xMax: max,
        borderColor: '#898E9A',
        borderWidth: 4,
        yMin: -0.039,
        yMax: 0.039,
        z: 1,
      },
      // value of top confidential cases
      {
        id: 'topConfidentialCasesValue',
        type: 'label',
        content: formatCurrency(data.overallMaximumPublic),
        xScaleID: 'x',
        xValue: data.overallMaximumPublic,
        yAdjust: 45,
        font: {
          family: 'Lato',
          size: 16,
          weight: '600',
        },
        position: (context: PartialEventContext) =>
          calculateLabelPosition(context, 'center'),
      },
    );
    //backup: confidential rendering for: (data.overallMaximumPublic > data.highestQuartileAverageNoZeros)
  }
  if (data.middleHalfMeanNoZeros && displayPositionMiddleHalf != null) {
    annotations.push(
      {
        id: 'middleHalfMeanLabelText',
        type: 'label',
        content: ['Middle-Half', 'Mean'],
        xScaleID: 'x',
        xMin: displayPositionMiddleHalf,
        xMax: displayPositionMiddleHalf,
        yAdjust: -80,
        font: {
          family: 'Lato',
          size: 14,
          weight: '500',
        },
        position: (context: PartialEventContext) =>
          calculateLabelPosition(context, 'center'),
      },
      {
        id: 'middleHalfMeanLabel',
        type: 'label',
        content: formatCurrency(middleHalfMeanValue),
        xScaleID: 'x',
        xMin: displayPositionMiddleHalf,
        xMax: displayPositionMiddleHalf,
        yAdjust: -45,
        font: {
          family: 'Lato',
          size: 14,
          weight: '600',
        },
      },
      {
        id: 'middleHalfMeanValueLabel',
        type: 'line',
        xScaleID: 'x',
        xMin: displayPositionMiddleHalf,
        xMax: displayPositionMiddleHalf,
        borderColor: '#000000',
        borderWidth: 2,
        yScaleID: 'annotationY',
        yMin: -0.1,
        yMax: 0.039,
        z: 2,
      },
    );
  }

  if (data.overallAverageNoZeros && displayPositionMean != null) {
    annotations.push(
      {
        id: 'meanLabelText',
        type: 'label',
        content: ['Mean'],
        xScaleID: 'x',
        xMin: displayPositionMean,
        xMax: displayPositionMean,
        yAdjust: 70,
        font: {
          family: 'Lato',
          size: 14,
          weight: '500',
        },
        position: (context: PartialEventContext) =>
          calculateLabelPosition(context, 'center'),
      },
      {
        id: 'meanLabel',
        type: 'label',
        content: formatCurrency(meanValue),
        xScaleID: 'x',
        xMin: displayPositionMean,
        xMax: displayPositionMean,
        yAdjust: 45,
        font: {
          family: 'Lato',
          size: 16,
          weight: '600',
        },
      },
      {
        id: 'meanValueLabel',
        type: 'line',
        xScaleID: 'x',
        xMin: displayPositionMean,
        xMax: displayPositionMean,
        borderColor: '#000000',
        borderWidth: 2,
        yScaleID: 'annotationY',
        z: 2,
        yMin: -0.039,
        yMax: 0.1,
      },
    );
  }
  return annotations;
};

export const prepareStatisticsData = (data: IRequiredStatistics) => {
  let entries = toPairs(data);

  entries = entries.map(([key, value]) => {
    const shouldNotModify = [
      'bottomConfidentialCasesCount',
      'topConfidentialCasesCount',
    ].includes(key);

    if (shouldNotModify) {
      return [key, value];
    }

    const currentValue = value || MIN_DISPLAYABLE_VALUE;
    return [key, currentValue];
  });

  return fromPairs(entries) as IRequiredStatistics;
};

export const buildOptions = (
  data: IRequiredStatistics,
  annotations: Array<AnnotationOptions>,
): ChartOptions<'bar'> => {
  const { min, max } = getDataValues(data);

  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    animation: false,
    scales: {
      annotationY: {
        display: false,
        stacked: true,
        axis: 'y',
      },
      y: {
        display: false,
      },
      x: {
        type: 'logarithmic',
        position: 'center',
        border: {
          width: 2,
          color: '#898E9A',
        },
        ticks: {
          display: false,
        },
        min: Math.max(min, MIN_LOG_AXIS_VALUE),
        max: Math.max(max, MIN_LOG_AXIS_VALUE * 10),
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      annotation: {
        clip: false,
        annotations,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        onClick: noop,
        labels: {
          generateLabels,
          boxWidth: 24,
          boxHeight: 24,
          font: {
            family: 'Lato',
            size: 16,
            weight: '300',
          },
        },
      },
    },
  };
};

export const buildData = (
  data: IRequiredStatistics,
  t: TFunction,
): ChartData<'bar'> => {
  const {
    publicMin,
    publicMax,
    displayPositionLowestQuartile,
    displayPositionHighestQuartile,
  } = getDataValues(data);

  const datasets: Array<ChartDataset<'bar'>> = [
    {
      type: 'bar',
      label: t('acceptableRangeLegend'),
      data: [[displayPositionLowestQuartile, displayPositionHighestQuartile]],
      borderWidth: 2,
      borderColor: '#E5B045',
      backgroundColor: '#FFEBC2',
      borderSkipped: false,
      barPercentage: 0.1,
      datalabels: {
        display: false,
      },
    },
  ];
  if (data.bottomConfidentialCasesCount) {
    datasets.push({
      type: 'bar',
      label: t('confidentialCasesLegend'),
      data: [[calculateMin(publicMin), publicMin]],
      borderWidth: 2,
      borderColor: '#BCBEC3',
      backgroundColor: '#DDDFE3',
      borderSkipped: false,
      barPercentage: 0.1,
      datalabels: {
        display: false,
      },
    });
  }

  if (data.topConfidentialCasesCount) {
    datasets.push({
      type: 'bar',
      label: t('confidentialCasesLegend'),
      data: [[publicMax, calculateMax(publicMax)]],
      borderWidth: 2,
      borderColor: '#BCBEC3',
      backgroundColor: '#DDDFE3',
      borderSkipped: false,
      barPercentage: 0.1,
      datalabels: {
        display: false,
      },
    });
  }

  return {
    labels: [''],
    datasets,
  };
};

const CONFIDENTIAL_COUNT_WIDTH_RATIO = 0.3;

const calculateMin = (publicMin: number) => {
  // If the order of public min is greater or equal than zero,
  // the distance between min value and public min value is equal to
  // width of one order of magnitude multiplied by CONFIDENTIAL_COUNT_WIDTH_RATIO.
  if (publicMin <= 0 || !Number.isFinite(publicMin)) {
    return MIN_LOG_AXIS_VALUE;
  }

  if (Math.log10(publicMin) < 1) {
    return MIN_LOG_AXIS_VALUE;
  }

  return 10 ** (Math.log10(publicMin) - CONFIDENTIAL_COUNT_WIDTH_RATIO);
};

const calculateMax = (publicMax: number) => {
  // The distance between public max and max value is equal to
  // width of one order of magnitude multiplied by CONFIDENTIAL_COUNT_WIDTH_RATIO.
  if (publicMax <= 0 || !Number.isFinite(publicMax)) {
    return MIN_LOG_AXIS_VALUE * 10;
  }

  return 10 ** (Math.log10(publicMax) + CONFIDENTIAL_COUNT_WIDTH_RATIO);
};
